import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { inviteApi } from '../../lib/inviteApi';

const TILES = [
  { key: 'searches', label: 'Searches', tone: 'cyan' },
  { key: 'prospects_found', label: 'Prospects found', tone: 'blue' },
  { key: 'active_contacts', label: 'Active contacts', tone: 'blue' },
  { key: 'invited', label: 'Invited', tone: 'violet' },
  { key: 'sent', label: 'Sent', tone: 'green' },
  { key: 'delivered', label: 'Delivered', tone: 'green' },
  { key: 'opened', label: 'Opened', tone: 'green' },
  { key: 'clicked', label: 'Clicked', tone: 'green' },
  { key: 'replied', label: 'Replied', tone: 'violet' },
  { key: 'interested', label: 'Interested', tone: 'violet' },
  { key: 'ticket_requested', label: 'Ticket requested', tone: 'gold' },
  { key: 'converted', label: 'Converted', tone: 'gold' },
  { key: 'bounced', label: 'Bounced', tone: 'red' },
  { key: 'unsubscribed', label: 'Unsubscribed', tone: 'red' },
  { key: 'suppressed', label: 'Suppressed', tone: 'red' },
  { key: 'archived', label: 'Archived', tone: 'gray' },
  { key: 'draft_threads', label: 'Drafts', tone: 'gray' },
  { key: 'followups_due', label: 'Follow-ups due', tone: 'cyan' },
];

const TONES = {
  cyan:   { color: '#90E0EF', border: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.06)', glow: 'rgba(144,224,239,0.35)' },
  blue:   { color: '#7aa8d4', border: 'rgba(122,168,212,0.4)',  bg: 'rgba(122,168,212,0.05)', glow: 'rgba(122,168,212,0.28)' },
  green:  { color: '#7ed4a0', border: 'rgba(126,212,160,0.45)', bg: 'rgba(126,212,160,0.06)', glow: 'rgba(126,212,160,0.28)' },
  violet: { color: '#c89dff', border: 'rgba(200,157,255,0.42)', bg: 'rgba(200,157,255,0.06)', glow: 'rgba(200,157,255,0.32)' },
  gold:   { color: '#e6c562', border: 'rgba(230,197,98,0.45)',  bg: 'rgba(230,197,98,0.06)',  glow: 'rgba(230,197,98,0.32)' },
  red:    { color: '#e88ba0', border: 'rgba(232,139,160,0.42)', bg: 'rgba(232,139,160,0.05)', glow: 'rgba(232,139,160,0.28)' },
  gray:   { color: '#9a9a9a', border: '#333',                   bg: 'transparent',            glow: 'rgba(255,255,255,0.08)' },
};

// Map an overview metric key → recent_records key + time_series key (may be undefined).
const METRIC_BINDINGS = {
  searches: { records: 'searches', series: 'searches' },
  prospects_found: { records: 'prospects_found', series: 'prospects_found' },
  active_contacts: { records: null, series: null },
  invited: { records: null, series: null },
  sent: { records: 'sent', series: 'sent' },
  delivered: { records: 'sent', series: 'sent' },
  opened: { records: 'sent', series: 'sent' },
  clicked: { records: 'sent', series: 'sent' },
  replied: { records: 'replied', series: 'replied' },
  interested: { records: null, series: null },
  ticket_requested: { records: 'ticket_requested', series: 'ticket_requested' },
  converted: { records: 'ticket_requested', series: 'ticket_requested' },
  bounced: { records: null, series: null },
  unsubscribed: { records: 'unsubscribed', series: 'unsubscribed' },
  suppressed: { records: 'unsubscribed', series: 'unsubscribed' },
  archived: { records: null, series: null },
  draft_threads: { records: null, series: null },
  followups_due: { records: null, series: null },
};

const RANGE_OPTIONS = [
  { label: '7 days', days: 7 },
  { label: '14 days', days: 14 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

export default function AnalyticsTab({ exhibitionId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState('searches');
  const [days, setDays] = useState(30);

  const load = async () => {
    if (!exhibitionId) return;
    setLoading(true);
    try {
      const d = await inviteApi.dashboard(exhibitionId, { days });
      setData(d);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [exhibitionId, days]);

  const overview = data?.overview || {};
  const binding = METRIC_BINDINGS[selected] || {};
  const seriesKey = binding.series;
  const recordsKey = binding.records;
  const series = (seriesKey && data?.time_series?.[seriesKey]) || [];
  const records = (recordsKey && data?.recent_records?.[recordsKey]) || [];

  const tileTone = useMemo(() => {
    const t = TILES.find((x) => x.key === selected);
    return TONES[t?.tone || 'cyan'];
  }, [selected]);

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {/* Top controls */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-mono text-[#777] tracking-[0.18em] uppercase">Range</span>
        {RANGE_OPTIONS.map((r) => (
          <button
            key={r.days}
            onClick={() => setDays(r.days)}
            data-clickable="true"
            data-testid={`analytics-range-${r.days}`}
            className={`text-[10px] font-mono px-2.5 py-1 border uppercase tracking-[0.12em] transition-colors ${
              days === r.days
                ? 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/5'
                : 'border-[#222] text-[#777] hover:text-white hover:border-[#444]'
            }`}
          >
            {r.label}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 text-[10px] font-mono text-[#666]">
          {loading && <Loader2 className="w-3 h-3 animate-spin"/>}
          {loading ? 'Loading…' : `${data?.overview?.searches ?? 0} searches · ${data?.overview?.active_contacts ?? 0} contacts`}
        </div>
      </div>

      {/* Overview tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {TILES.map((t) => {
          const tone = TONES[t.tone];
          const isActive = selected === t.key;
          const v = overview[t.key] ?? 0;
          return (
            <button
              key={t.key}
              onClick={() => setSelected(t.key)}
              data-clickable="true"
              data-testid={`analytics-tile-${t.key}`}
              className="text-left border p-3 transition-all"
              style={{
                borderColor: isActive ? tone.border : '#1a1a1a',
                background: isActive ? tone.bg : '#0a0a0a',
                boxShadow: isActive ? `0 0 20px ${tone.glow}` : 'none',
              }}
            >
              <p className="text-[9px] font-mono tracking-[0.18em] uppercase truncate" style={{ color: isActive ? tone.color : '#777' }}>{t.label}</p>
              <p className="font-outfit text-2xl tracking-tight mt-1 tabular-nums" style={{ color: isActive ? tone.color : '#fff' }}>{v}</p>
            </button>
          );
        })}
      </div>

      {/* Chart + records */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-[#1a1a1a] bg-[#0a0a0a] p-4 min-h-[280px]">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] font-mono tracking-[0.18em] uppercase" style={{ color: tileTone.color }}>
              {TILES.find((t) => t.key === selected)?.label || selected} · {days}d
            </p>
            <p className="text-[10px] font-mono text-[#666]">{series.length} day{series.length === 1 ? '' : 's'} with data</p>
          </div>
          <BarChart data={series} days={days} tone={tileTone} />
        </div>

        <div className="border border-[#1a1a1a] bg-[#0a0a0a]">
          <div className="px-4 py-2.5 border-b border-[#1a1a1a]">
            <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#777]">Recent · {TILES.find((t) => t.key === selected)?.label}</p>
          </div>
          <div className="max-h-[40vh] overflow-y-auto">
            <RecordList records={records} kind={recordsKey} />
          </div>
        </div>
      </div>

      {/* Funnel */}
      {data?.funnel && (
        <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4">
          <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase mb-3">Outreach funnel</p>
          <Funnel funnel={data.funnel} />
        </div>
      )}

      {/* Breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <BreakdownBlock title="By category" rows={data?.by_category || []} labelKey="category" testid="analytics-by-category" />
        <BreakdownBlock title="By priority" rows={data?.by_priority || []} labelKey="priority" testid="analytics-by-priority" />
        <BreakdownBlock title="By thread status" rows={data?.by_thread_status || []} labelKey="status" testid="analytics-by-thread-status" />
      </div>
    </div>
  );
}

function BarChart({ data, days, tone }) {
  if (!data || !data.length) {
    return <p className="text-xs font-mono text-[#555] py-12 text-center">No time data yet for this metric.</p>;
  }
  // Fill missing days so the chart doesn't jump
  const today = new Date();
  const filled = [];
  const byDate = new Map(data.map((d) => [d.date, d.count]));
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    filled.push({ date: key, count: byDate.get(key) || 0 });
  }
  const max = Math.max(1, ...filled.map((d) => d.count));
  const width = 100;
  const height = 100;
  const barWidth = width / filled.length;
  return (
    <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="w-full h-[220px]">
      {filled.map((d, i) => {
        const h = (d.count / max) * (height - 14);
        const x = i * barWidth;
        const y = height - h - 12;
        return (
          <g key={d.date}>
            <rect
              x={x + barWidth * 0.15}
              y={y}
              width={barWidth * 0.7}
              height={h}
              fill={tone.color}
              opacity={d.count ? 0.85 : 0.05}
            >
              <title>{`${d.date} · ${d.count}`}</title>
            </rect>
          </g>
        );
      })}
      {/* baseline */}
      <line x1="0" x2={width} y1={height - 11} y2={height - 11} stroke="#222" strokeWidth="0.3" />
      {/* labels */}
      {filled.length <= 30 && filled.map((d, i) => (
        (i === 0 || i === filled.length - 1 || i === Math.floor(filled.length / 2)) && (
          <text key={d.date + '_l'} x={i * barWidth + barWidth / 2} y={height - 3} fontSize="3" textAnchor="middle" fill="#555" fontFamily="ui-monospace, monospace">
            {d.date.slice(5)}
          </text>
        )
      ))}
    </svg>
  );
}

function RecordList({ records, kind }) {
  if (!records || !records.length) return <p className="p-4 text-xs font-mono text-[#555]">No records yet.</p>;
  return (
    <div>
      {records.map((r, i) => (
        <div key={r.id || i} className="px-4 py-2.5 border-b border-[#0e0e0e]">
          <p className="text-xs font-mono text-white truncate">{primaryText(r, kind)}</p>
          <p className="text-[10px] font-mono text-[#666] mt-0.5">{secondaryText(r, kind)}</p>
        </div>
      ))}
    </div>
  );
}

function primaryText(r, kind) {
  if (kind === 'searches') return `${r.audience_type || '—'} · ${r.location_query || '—'}`;
  if (kind === 'prospects_found') return r.name || r.email || '—';
  if (kind === 'sent') return r.recipient_email || r.subject || '—';
  if (kind === 'replied') return r.sender_email || r.subject || '—';
  if (kind === 'ticket_requested') return r.email || r.name || '—';
  if (kind === 'unsubscribed') return r.email || '—';
  return r.email || r.name || r.id;
}
function secondaryText(r, kind) {
  const ts = r.created_at || r.sent_at || r.received_at;
  const when = ts ? new Date(ts).toLocaleString() : '';
  if (kind === 'searches') return `${r.status} · ${r.result_count || 0} results · ${when}`;
  if (kind === 'sent') return `${r.message_type || ''} · ${r.status || ''} · ${when}`;
  if (kind === 'unsubscribed') return `${r.reason || ''} · ${when}`;
  return when;
}

function Funnel({ funnel }) {
  const steps = [
    { key: 'prospects_found', label: 'Prospects found', tone: 'blue' },
    { key: 'saved_prospects', label: 'Saved', tone: 'violet' },
    { key: 'converted_to_invite', label: 'Converted', tone: 'cyan' },
    { key: 'sent', label: 'Sent', tone: 'green' },
    { key: 'replied', label: 'Replied', tone: 'violet' },
    { key: 'ticket_requested', label: 'Ticket requested', tone: 'gold' },
    { key: 'ticket_accepted', label: 'Accepted', tone: 'gold' },
  ];
  const max = Math.max(1, ...steps.map((s) => funnel[s.key] || 0));
  return (
    <div className="space-y-2">
      {steps.map((s) => {
        const v = funnel[s.key] || 0;
        const pct = (v / max) * 100;
        const tone = TONES[s.tone];
        return (
          <div key={s.key} className="flex items-center gap-3">
            <span className="text-[10px] font-mono text-[#999] uppercase tracking-[0.14em] w-[140px] flex-shrink-0">{s.label}</span>
            <div className="flex-1 h-3 bg-[#0e0e0e] relative">
              <motion.div animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} className="h-full" style={{ background: tone.color, opacity: 0.7 }} />
            </div>
            <span className="text-[11px] font-mono tabular-nums w-[40px] text-right" style={{ color: tone.color }}>{v}</span>
          </div>
        );
      })}
    </div>
  );
}

function BreakdownBlock({ title, rows, labelKey, testid }) {
  if (!rows || !rows.length) {
    return (
      <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4" data-testid={testid}>
        <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase mb-2">{title}</p>
        <p className="text-xs font-mono text-[#555]">No data yet.</p>
      </div>
    );
  }
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-4" data-testid={testid}>
      <p className="text-[10px] font-mono text-[#90E0EF] tracking-[0.22em] uppercase mb-2">{title}</p>
      <div className="space-y-1.5">
        {rows.slice(0, 10).map((r) => (
          <div key={r[labelKey]} className="flex items-center gap-2">
            <span className="text-[10px] font-mono text-[#aaa] uppercase tracking-[0.14em] w-[100px] truncate flex-shrink-0">{(r[labelKey] || '').replace(/_/g, ' ')}</span>
            <div className="flex-1 h-1.5 bg-[#101010]">
              <motion.div animate={{ width: `${(r.count / max) * 100}%` }} transition={{ duration: 0.4 }} className="h-full bg-[#90E0EF]" style={{ opacity: 0.7 }} />
            </div>
            <span className="text-[10px] font-mono text-white tabular-nums w-[30px] text-right">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
